import { useMemo } from 'react';
import {
    createBestellpositionViewSchemaDraftBestellung,
    createDateSchema,
    createTimeSchema,
} from '../../shared/validation/bestellung/validation';
import { minDate } from '../../shared/helper/date-helper';
import * as Yup from 'yup';
import { useSelector } from 'react-redux';
import { selectIsDebitor, selectIsFarmleiter } from '../../store/Navigation.store';
import { datumWEBisSchema, uhrzeitWEBisSchema } from '../../shared/validation/bestellung/schemas';
import { WUNSCHTERMIN_VALIDATION_ERRORS } from '../../shared/constants';
import { testDebitorWunschterminInFuture } from '../../shared/validation/bestellung/testFunctions';

function createNonDraftBestellungValidationSchema(
    isDebitor: boolean,
    overWeightConfirmed: boolean,
    minDate: string,
    isFarmleiter: boolean
) {
    return Yup.object({
        notificationCheck: Yup.boolean(),
        bestellpositionenView: Yup.object().when('notificationCheck', {
            is: true,
            then: () => {
                return createBestellpositionViewSchemaDraftBestellung(isDebitor, false, isFarmleiter);
            },
            otherwise: () => {
                return createBestellpositionViewSchemaDraftBestellung(isDebitor, true, isFarmleiter);
            },
        }),
        lieferbedingung: Yup.string().required(),
        datumDebitor: createDateSchema(minDate),
        uhrzeitDebitor: createTimeSchema().test(
            'debitor-wunschtermin-in-future',
            WUNSCHTERMIN_VALIDATION_ERRORS.TIME_IN_THE_PAST,
            testDebitorWunschterminInFuture
        ),
        datumWE: createDateSchema(minDate, false),
        uhrzeitWE: createTimeSchema(false),
        datumWEVon: createDateSchema(minDate, false),
        datumWEBis: datumWEBisSchema,
        uhrzeitWEVon: createTimeSchema(false),
        uhrzeitWEBis: uhrzeitWEBisSchema,
    });
}

function createDraftBestellungValidationSchema(isDebitor: boolean, isFarmleiter: boolean, overweightConfirmed: boolean) {
    const bestellpositionenViewSchema = createBestellpositionViewSchemaDraftBestellung(isDebitor, !overweightConfirmed, isFarmleiter);

    // Basis-Schema immer: Bestellpositionen (mit/ohne Übergewichts-Check)
    // Zusätzlich für Debitor:innen: Wunschtermin (Datum + Uhrzeit) auch im Entwurf validieren
    if (isDebitor) {
        return Yup.object({
            bestellpositionenView: bestellpositionenViewSchema,
            datumDebitor: createDateSchema(minDate),
            uhrzeitDebitor: createTimeSchema().test(
                'debitor-wunschtermin-in-future',
                WUNSCHTERMIN_VALIDATION_ERRORS.TIME_IN_THE_PAST,
                testDebitorWunschterminInFuture
            ),
        });
    }

    return Yup.object({
        bestellpositionenView: bestellpositionenViewSchema,
    });
}

// NOTE: Ignore because we could not get yup typings working correctly
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const useAuftragskorbValidationSchema = (overWeightConfirmed: boolean, saveAsDraft: boolean, storniereAuftrag: boolean) => {
    const isDebitor = useSelector(selectIsDebitor);
    const isFarmleiter = useSelector(selectIsFarmleiter);

    return useMemo(() => {
        if (!storniereAuftrag) {
            if (saveAsDraft) {
                return createDraftBestellungValidationSchema(isDebitor, isFarmleiter, overWeightConfirmed);
            } else {
                return createNonDraftBestellungValidationSchema(isDebitor, overWeightConfirmed, minDate, isFarmleiter);
            }
        }
        return null;
    }, [isDebitor, isFarmleiter, overWeightConfirmed, saveAsDraft, storniereAuftrag]);
};
